import { toZonedTime, fromZonedTime, formatInTimeZone } from 'date-fns-tz';
import { format } from 'date-fns';

/**
 * Get user's local browser timezone
 * Example: 'Australia/Brisbane', 'America/New_York', 'Europe/London'
 */
export const getUserTimezone = (): string => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/**
 * Convert a local date/time from a specific timezone to UTC
 * 
 * @param date - The date object (in local timezone context)
 * @param time - Time string in HH:mm format (e.g., "14:30")
 * @param timezone - Source timezone (e.g., 'Australia/Perth')
 * @returns Object with UTC date string (yyyy-MM-dd) and time string (HH:mm:ss)
 */
export const convertToUTC = (
  date: Date,
  time: string,
  timezone: string
): { utcDate: string; utcTime: string } => {
  try {
    const match = time.trim().match(/^(\d{2}):(\d{2})(?::(\d{2}))?$/);
    if (!match) {
      throw new Error(`Invalid time format: "${time}"`);
    }
    const hh = match[1];
    const mm = match[2];
    const ss = match[3] ?? '00';
    const normalizedTime = `${hh}:${mm}:${ss}`;

    const dateString = format(date, 'yyyy-MM-dd');
    const utcDateTime = fromZonedTime(`${dateString} ${normalizedTime}`, timezone);

    return {
      utcDate: formatInTimeZone(utcDateTime, 'UTC', 'yyyy-MM-dd'),
      utcTime: formatInTimeZone(utcDateTime, 'UTC', 'HH:mm:ss')
    };
  } catch (error) {
    console.error('Error converting to UTC:', error);
    const safeTime = /^\d{2}:\d{2}:\d{2}$/.test(time) ? time : `${time}:00`;
    return {
      utcDate: format(date, 'yyyy-MM-dd'),
      utcTime: safeTime
    };
  }
};

/**
 * Convert UTC date/time to a specific timezone
 * 
 * @param utcDate - UTC date string (yyyy-MM-dd format)
 * @param utcTime - UTC time string (HH:mm:ss or HH:mm format)
 * @param targetTimezone - Target timezone (e.g., 'Australia/Brisbane')
 * @returns Object with local Date, time string (HH:mm), and date string
 */
export const convertFromUTC = (
  utcDate: string,
  utcTime: string,
  targetTimezone: string
): { localDate: Date; localTime: string; localDateString: string } => {
  try {
    const match = utcTime.trim().match(/^(\d{2}):(\d{2})(?::(\d{2}))?$/);
    if (!match) {
      throw new Error(`Invalid UTC time format: "${utcTime}"`);
    }
    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    const seconds = Number(match[3] ?? '0');

    const utcDateTime = new Date(utcDate);
    utcDateTime.setUTCHours(hours, minutes, seconds, 0);

    const localDateTime = toZonedTime(utcDateTime, targetTimezone);

    return {
      localDate: localDateTime,
      localTime: format(localDateTime, 'HH:mm'),
      localDateString: format(localDateTime, 'yyyy-MM-dd')
    };
  } catch (error) {
    console.error('Error converting from UTC:', error);
    const fallbackDate = new Date(utcDate);
    return {
      localDate: fallbackDate,
      localTime: utcTime.substring(0, 5),
      localDateString: utcDate
    };
  }
};

/**
 * Convert availability slots from mentor timezone to mentee timezone
 * Used for displaying mentor's recurring weekly availability to mentees
 */
export const convertAvailabilitySlotsToTimezone = (
  slots: Array<{ start_time: string; end_time: string }>,
  date: Date,
  mentorTimezone: string,
  menteeTimezone: string = getUserTimezone()
): Array<{ start_time: string; end_time: string; crosses_midnight: boolean }> => {
  // If timezones are the same, no conversion needed
  if (mentorTimezone === menteeTimezone) {
    return slots.map(slot => ({
      start_time: slot.start_time.substring(0, 5), // Trim to HH:mm
      end_time: slot.end_time.substring(0, 5),
      crosses_midnight: slot.end_time < slot.start_time
    }));
  }

  return slots.map(slot => {
    // Convert start time
    const startUTC = convertToUTC(date, slot.start_time, mentorTimezone);
    const startLocal = convertFromUTC(startUTC.utcDate, startUTC.utcTime, menteeTimezone);
    
    // Convert end time
    const endUTC = convertToUTC(date, slot.end_time, mentorTimezone);
    const endLocal = convertFromUTC(endUTC.utcDate, endUTC.utcTime, menteeTimezone);
    
    // Check if it crosses midnight (day boundary)
    const crosses_midnight = startLocal.localDateString !== endLocal.localDateString;
    
    return {
      start_time: startLocal.localTime,
      end_time: endLocal.localTime,
      crosses_midnight
    };
  });
};

/**
 * Format timezone abbreviation for display
 * Examples: "AEST", "GMT+10", "EST", "PST"
 */
export const getTimezoneAbbreviation = (timezone: string, date: Date = new Date()): string => {
  try {
    const formatted = formatInTimeZone(date, timezone, 'zzz');
    return formatted;
  } catch (error) {
    console.error('Error formatting timezone:', error);
    return timezone; // Fallback to full timezone name
  }
};
